import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { AlertTriangle, Clock, Send, ShieldOff } from 'lucide-react'

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700',
  high:   'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low:    'bg-muted text-muted-foreground',
}
const STATUS_COLORS: Record<string, string> = {
  open:        'bg-blue-100 text-blue-700',
  in_progress: 'bg-violet-100 text-violet-700',
  resolved:    'bg-green-100 text-green-700',
  closed:      'bg-muted text-muted-foreground',
}

interface Props {
  platformRole: string
  sessionOnly?: boolean
}

export function SupportInboxView({ platformRole, sessionOnly = false }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const channelRef = useRef<any>(null)

  const { data: staff } = useQuery({
    queryKey: ['conorbit-staff'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('app_admins' as any)
        .select('id, first_name, last_name') as any)
      return (data ?? []) as any[]
    }
  })

  const { data: tickets } = useQuery({
    queryKey: ['support-tickets', platformRole],
    queryFn: async () => {
      let q = (supabase
        .from('support_tickets' as any)
        .select('*, organizations(name)')
        .order('created_at', { ascending: false }) as any)
      if (platformRole === 'support_agent') {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: admin } = await (supabase
            .from('app_admins' as any)
            .select('id')
            .eq('user_id', user.id)
            .single() as any)
          if (admin) q = q.eq('assigned_to', admin.id)
        }
      }
      const { data } = await q
      return (data ?? []) as any[]
    }
  })

  const { data: messages } = useQuery({
    queryKey: ['ticket-messages', selectedTicket?.id],
    enabled: !!selectedTicket,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_ticket_messages' as any)
        .select('*, sender:sender_id(full_name)')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at') as any)
      return (data ?? []) as any[]
    }
  })

  const { data: accessSession } = useQuery({
    queryKey: ['access-session', selectedTicket?.id],
    enabled: !!selectedTicket,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_access_sessions' as any)
        .select('*, agent:agent_id(first_name, last_name)')
        .eq('ticket_id', selectedTicket.id)
        .eq('status', 'active')
        .maybeSingle() as any)
      return data as any
    }
  })

  const { data: accessLog } = useQuery({
    queryKey: ['access-log', selectedTicket?.id],
    enabled: !!selectedTicket && !!accessSession,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_access_log' as any)
        .select('*')
        .eq('session_id', accessSession?.id)
        .order('created_at', { ascending: false })
        .limit(50) as any)
      return (data ?? []) as any[]
    }
  })

  // Realtime access log subscription
  useEffect(() => {
    if (!accessSession?.id) return
    const channel = supabase
      .channel(`access-log-${accessSession.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_access_log',
        filter: `session_id=eq.${accessSession.id}`,
      }, () => {
        qc.invalidateQueries({ queryKey: ['access-log', selectedTicket?.id] })
      })
      .subscribe()
    channelRef.current = channel
    return () => { channel.unsubscribe() }
  }, [accessSession?.id, selectedTicket?.id, qc])

  const sendReply = async () => {
    if (!reply.trim() || !selectedTicket) return
    setSending(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase
        .from('support_ticket_messages' as any)
        .insert({
          ticket_id: selectedTicket.id,
          sender_id: user?.id,
          body: reply.trim(),
          sender_type: 'conorbit',
        }) as any)
      setReply('')
      qc.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket.id] })
    } catch {
      toast({ title: 'Failed to send reply.', variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  const revokeAccess = async () => {
    if (!accessSession) return
    setRevoking(true)
    try {
      await supabase.functions.invoke('revoke-support-access', {
        body: { session_id: accessSession.id }
      })
      toast({ title: 'Support access revoked.' })
      qc.invalidateQueries({ queryKey: ['access-session', selectedTicket?.id] })
    } catch {
      toast({ title: 'Failed to revoke access.', variant: 'destructive' })
    } finally {
      setRevoking(false)
    }
  }

  const updateStatus = async (status: string) => {
    if (!selectedTicket) return
    await (supabase
      .from('support_tickets' as any)
      .update({ status })
      .eq('id', selectedTicket.id) as any)
    qc.invalidateQueries({ queryKey: ['support-tickets', platformRole] })
  }

  const assignTo = async (adminId: string) => {
    if (!selectedTicket) return
    await (supabase
      .from('support_tickets' as any)
      .update({ assigned_to: adminId })
      .eq('id', selectedTicket.id) as any)
    qc.invalidateQueries({ queryKey: ['support-tickets', platformRole] })
  }

  const expiryCountdown = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now()
    if (diff <= 0) return 'Expired'
    const h = Math.floor(diff / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    return `${h}h ${m}m`
  }

  const displayTickets = sessionOnly
    ? (tickets ?? []).filter((t: any) => t.support_access_granted)
    : (tickets ?? [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {sessionOnly ? 'Active Sessions' : platformRole === 'support_agent' ? 'My Tickets' : 'Support Inbox'}
        </h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">
          {sessionOnly
            ? 'Organisations with active support access sessions.'
            : 'Manage support tickets from client organisations.'}
        </p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-4 py-3 font-medium text-muted-foreground">Subject</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Organisation</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Priority</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Created</th>
            </tr>
          </thead>
          <tbody>
            {displayTickets.map((ticket: any) => (
              <tr
                key={ticket.id}
                className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                onClick={() => setSelectedTicket(ticket)}
              >
                <td className="px-4 py-3 font-medium">{ticket.subject}</td>
                <td className="px-4 py-3 text-muted-foreground">{ticket.organizations?.name}</td>
                <td className="px-4 py-3">
                  <Badge className={cn('text-[10px]', PRIORITY_COLORS[ticket.priority] ?? PRIORITY_COLORS.low)}>
                    {ticket.priority}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge className={cn('text-[10px]', STATUS_COLORS[ticket.status] ?? STATUS_COLORS.open)}>
                    {ticket.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {new Date(ticket.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {displayTickets.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No tickets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Ticket detail sheet */}
      <Sheet open={!!selectedTicket} onOpenChange={o => !o && setSelectedTicket(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto" side="right">
          {selectedTicket && (
            <div className="space-y-5">
              <SheetHeader>
                <SheetTitle className="text-base">{selectedTicket.subject}</SheetTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={PRIORITY_COLORS[selectedTicket.priority] ?? PRIORITY_COLORS.low}>
                    {selectedTicket.priority}
                  </Badge>
                  <Badge className={STATUS_COLORS[selectedTicket.status] ?? STATUS_COLORS.open}>
                    {selectedTicket.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{selectedTicket.organizations?.name}</span>
                </div>
              </SheetHeader>

              {/* Status + assign */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-justify text-xs text-muted-foreground mb-1">Status</p>
                  <Select defaultValue={selectedTicket.status} onValueChange={updateStatus}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-justify text-xs text-muted-foreground mb-1">Assigned to</p>
                  <Select
                    defaultValue={selectedTicket.assigned_to ?? ''}
                    onValueChange={assignTo}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {(staff ?? []).map((s: any) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.first_name} {s.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active access session banner */}
              {accessSession && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-800 text-sm font-medium">
                      <Clock className="w-4 h-4" />
                      Support access active: {accessSession.agent?.first_name} {accessSession.agent?.last_name}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={revokeAccess}
                      disabled={revoking}
                    >
                      <ShieldOff className="w-3 h-3 mr-1" />
                      {revoking ? 'Revoking…' : 'Revoke'}
                    </Button>
                  </div>
                  <p className="text-justify text-xs text-green-700">
                    Expires: {accessSession.expires_at ? expiryCountdown(accessSession.expires_at) : ''}
                  </p>
                  {(accessLog ?? []).length > 0 && (
                    <div className="mt-2 border-t border-green-200 pt-2 space-y-1 max-h-40 overflow-y-auto">
                      {(accessLog ?? []).map((log: any) => (
                        <div key={log.id} className="text-[11px] text-green-800 flex items-start gap-2">
                          <span className="text-green-500 shrink-0">
                            {new Date(log.created_at).toLocaleTimeString()}
                          </span>
                          <span>{log.action}: {log.affected_resource}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Message thread */}
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {(messages ?? []).map((msg: any) => (
                  <div
                    key={msg.id}
                    className={cn('flex', msg.sender_type === 'conorbit' ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] rounded-xl px-4 py-2.5 text-sm',
                        msg.sender_type === 'conorbit'
                          ? 'bg-[#0D2240] text-white'
                          : 'bg-muted text-foreground'
                      )}
                    >
                      <p>{msg.body}</p>
                      <p className={cn(
                        'text-[10px] mt-1',
                        msg.sender_type === 'conorbit' ? 'text-white/50' : 'text-muted-foreground'
                      )}>
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                {(messages ?? []).length === 0 && (
                  <p className="text-justify text-sm text-muted-foreground text-center py-4">No messages yet.</p>
                )}
              </div>

              {/* Reply */}
              <div className="flex gap-2">
                <Textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Type a reply…"
                  className="min-h-[80px] text-sm resize-none"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply()
                  }}
                />
                <Button
                  className="self-end shrink-0"
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
